// IndexedDB, hand-rolled. No wrapper library: this is about 120 lines of
// promise plumbing and the service worker has to read the same database in
// plain JavaScript, so a dependency here would have to exist twice.
//
// The store names and the outbox record shape are also known to public/sw.js.
// Change them in both places.

export const DB_NAME = 'lift';
export const DB_VERSION = 1;

export const STORE = {
  meta: 'meta',
  outbox: 'outbox',
  dead: 'dead',
  sets: 'sets',
  sessions: 'sessions',
  history: 'history',
} as const;

/** A queued write, replayed against POST /api/sync in sequence order. */
export interface OutboxRecord {
  seq?: number;
  /** unique id of this queued op, used to correlate the server's per-op result */
  id: string;
  op: 'session.upsert' | 'session.finish' | 'set.upsert' | 'set.delete' | 'flag.create';
  payload: Record<string, unknown>;
  at: string;
  tries: number;
  lastError?: string;
}

let dbPromise: Promise<IDBDatabase> | null = null;

export function openDb(): Promise<IDBDatabase> {
  if (dbPromise) return dbPromise;

  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);

    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE.meta)) {
        db.createObjectStore(STORE.meta, { keyPath: 'k' });
      }
      if (!db.objectStoreNames.contains(STORE.outbox)) {
        db.createObjectStore(STORE.outbox, { keyPath: 'seq', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE.dead)) {
        db.createObjectStore(STORE.dead, { keyPath: 'seq', autoIncrement: true });
      }
      if (!db.objectStoreNames.contains(STORE.sets)) {
        const s = db.createObjectStore(STORE.sets, { keyPath: 'id' });
        s.createIndex('by_session', 'session_id');
      }
      if (!db.objectStoreNames.contains(STORE.sessions)) {
        db.createObjectStore(STORE.sessions, { keyPath: 'id' });
      }
      if (!db.objectStoreNames.contains(STORE.history)) {
        const h = db.createObjectStore(STORE.history, { keyPath: 'id' });
        h.createIndex('by_started', 'started_at');
      }
    };

    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB open failed'));
    req.onblocked = () => reject(new Error('indexedDB is blocked by another tab'));
  });

  return dbPromise;
}

function wrap<T>(req: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error('indexedDB request failed'));
  });
}

/**
 * Run `fn` inside one transaction and resolve when that transaction commits.
 *
 * Resolving on commit rather than on the last request matters: a set and its
 * outbox entry are written together, and "saved" has to mean both reached disk.
 */
export async function tx<T>(
  stores: string[],
  mode: IDBTransactionMode,
  fn: (t: IDBTransaction) => Promise<T> | T,
): Promise<T> {
  const db = await openDb();
  return new Promise<T>((resolve, reject) => {
    const t = db.transaction(stores, mode);
    let result: T;
    let failed: unknown = null;

    t.oncomplete = () => (failed ? reject(failed) : resolve(result));
    t.onerror = () => reject(t.error ?? failed ?? new Error('transaction failed'));
    t.onabort = () => reject(t.error ?? failed ?? new Error('transaction aborted'));

    (async () => {
      try {
        result = await fn(t);
      } catch (err) {
        failed = err;
        try {
          t.abort();
        } catch {
          // already finishing
        }
      }
    })();
  });
}

// --- meta ---------------------------------------------------------------

export async function metaGet<T>(key: string): Promise<T | undefined> {
  const row = await tx([STORE.meta], 'readonly', (t) =>
    wrap<{ k: string; v: T } | undefined>(t.objectStore(STORE.meta).get(key)),
  );
  return row?.v;
}

export async function metaSet(key: string, value: unknown): Promise<void> {
  await tx([STORE.meta], 'readwrite', (t) => {
    t.objectStore(STORE.meta).put({ k: key, v: value });
  });
}

export async function metaDelete(key: string): Promise<void> {
  await tx([STORE.meta], 'readwrite', (t) => {
    t.objectStore(STORE.meta).delete(key);
  });
}

// --- generic helpers ----------------------------------------------------

export async function putAll(store: string, rows: unknown[]): Promise<void> {
  if (!rows.length) return;
  await tx([store], 'readwrite', (t) => {
    const s = t.objectStore(store);
    for (const row of rows) s.put(row);
  });
}

export async function getAll<T>(store: string): Promise<T[]> {
  return tx([store], 'readonly', (t) => wrap<T[]>(t.objectStore(store).getAll()));
}

export async function get<T>(store: string, key: IDBValidKey): Promise<T | undefined> {
  return tx([store], 'readonly', (t) => wrap<T | undefined>(t.objectStore(store).get(key)));
}

export async function byIndex<T>(store: string, index: string, key: IDBValidKey): Promise<T[]> {
  return tx([store], 'readonly', (t) =>
    wrap<T[]>(t.objectStore(store).index(index).getAll(key)),
  );
}

export async function clear(store: string): Promise<void> {
  await tx([store], 'readwrite', (t) => {
    t.objectStore(store).clear();
  });
}

export { wrap };
